import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { SbModalState } from '../modal-state.service';
import { MultiFreetextFilteredOptions } from '../modal.model';
import { API } from 'src/app/services/api/api';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { CullAtomAutocomplete } from 'src/app/components/culls/CullAtomAutoComplete';
import { UtilService } from 'src/app/services/util.service';
import { StringTransformationService } from '../../../transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-multi-freetext-filtered-modal',
  templateUrl: './sb-multi-freetext-filtered-modal.component.html',
  styleUrls: ['./sb-multi-freetext-filtered-modal.component.scss'],
  standalone: false,
})
export class SbMultiFreetextFilteredModalComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('inputElementElse') inputElementElse: ElementRef;
  @ViewChild('inputElementIf') inputElementIf: ElementRef;

  options: MultiFreetextFilteredOptions<string>;
  @Input() templateId: string;
  selectedOptions: string[] = [];
  selectionsAllowed: string[];
  selectedMode: string;

  cullAtomAutocomplete: CullAtomAutocomplete;

  searching = false;
  searchFailed = false;
  pasteFailed = false;

  // tslint:disable-next-line:variable-name
  _searchStr: string;

  set searchStr(value: string) {
    this._searchStr = value;
  }

  get searchStr(): string {
    return this._searchStr;
  }

  ngOnInit() {
    this.options = this.state.options.get(this.templateId) as MultiFreetextFilteredOptions<string>;
    this.selectedOptions = [];

    if (this.options.initial) {
      for (const option of this.options.initial) {
        this.selectedOptions.push(option);
      }
      this.selectedOptions.sort((a: string, b: string) => (a > b ? 1 : -1));
    }

    this.cullAtomAutocomplete = this.options.cullAtomAutoComplete;

    if (this.cullAtomAutocomplete?.cullAtom?.operator === 'Like') {
      this.selectedMode = 'Contains';
    }
  }

  constructor(
    private readonly state: SbModalState,
    private readonly api: API,
    private readonly util: UtilService
  ) {
    this.selectionsAllowed = ['Exact', 'Prefix', 'Suffix', 'Contains'];
    this.selectedMode = 'Exact';
  }

  ok() {
    if (this.checkOkEnabled()) {
      this.state.modals.get(this.templateId).close({ value: this.selectedOptions, cancel: false });
    }
  }

  enterPressed() {
    if (!this.searching) {
      if (this.searchStr) {
        this.addItem(this.searchStr);
        this.selectedOptions.sort((a: string, b: string) => (a > b ? 1 : -1));
      }
    }
  }

  selectedItem($event) {
    if (!this.searching && $event?.item) {
      this.addItem($event.item);
      this.selectedOptions.sort((a: string, b: string) => (a > b ? 1 : -1));
    }
  }

  addItem(option: string) {
    switch (this.selectedMode) {
      case 'Suffix':
        option = (!option.startsWith('%') ? '%' : '') + option;
        break;
      case 'Prefix':
        option = option + (!option.endsWith('%') ? '%' : '');
        break;
      case 'Contains':
        option = (!option.startsWith('%') ? '%' : '') + option + (!option.endsWith('%') ? '%' : '');
        break;
      case 'Exact':
      default:
        break;
    }
    if (!this.selectedOptions.includes(option)) {
      this.selectedOptions.push(option);
      this.searchStr = null;
    }
  }

  pasteDataFromClipboard() {
    if (!this.searching) {
      navigator.clipboard
        .readText()
        .then((input: string) => {
          const arr = input.match(/[^\r\n]+/g);
          const clearArr = arr.filter(x => x.trim() !== '');
          if (this.selectedOptions) {
            for (const option of clearArr) {
              this.addItem(option);
            }
          } else {
            this.selectedOptions = clearArr;
            this.searchStr = null;
          }
          this.selectedOptions.sort((a: string, b: string) => a.localeCompare(b));
          this.pasteFailed = false;
        })
        .catch(() => {
          this.pasteFailed = true;
          this.searchStr = null;
        });
    }
  }

  removeOption(option: string) {
    const isOptionInSelectedOptions = this.selectedOptions.includes(option);
    if ((option || option === '') && isOptionInSelectedOptions) {
      this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
    }
    this.selectedOptions.sort((a: string, b: string) => a.localeCompare(b));
  }

  removeAll() {
    this.selectedOptions = [];
  }

  checkOkEnabled() {
    return true;
  }

  clear() {
    this.selectedOptions = [];
  }

  cancel() {
    this.state.modals.get(this.templateId).close({ value: null, cancel: true });
  }

  search(pSearch: string): Observable<string[]> {
    if (pSearch.startsWith('%')) {
      pSearch = '%' + pSearch;
    }
    if (this.cullAtomAutocomplete) {
      return this.api
        .cs_Cull_freeTextAutocomplete(this.cullAtomAutocomplete.autocomplete, this.cullAtomAutocomplete.cullAtom, {
          search: this.stringTransformation.encodeSearchQueryParam(false, pSearch),
        })
        .pipe(
          map(response => {
            return response;
          })
        );
    }
  }

  searchTermAsResult(term) {
    return this.search(term).pipe(
      tap(response => {
        response.unshift(term);
      })
    );
  }

  dosearch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(0),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap(term =>
        this.searchTermAsResult(term).pipe(
          tap(() => {
            this.searchFailed = false;
            this.pasteFailed = false;
          }),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          })
        )
      ),
      tap(() => (this.searching = false))
    );

  insertEmptyValue() {
    if (!this.searching && !this.selectedOptions.indexOf('')) {
      this.selectedOptions.push('');
      this.selectedOptions.sort((a: string, b: string) => a.localeCompare(b));
    }
  }

  openSuggestions() {
    if (this.cullAtomAutocomplete) {
      this.inputElementIf.nativeElement.value = '';
      this.inputElementIf.nativeElement.dispatchEvent(new Event('input'));
      this.inputElementIf.nativeElement.focus();
    } else {
      this.inputElementElse.nativeElement.value = '';
      this.inputElementElse.nativeElement.dispatchEvent(new Event('input'));
      this.inputElementElse.nativeElement.focus();
    }
  }

  changeMode($event: string) {
    this.selectedMode = $event;
  }
}
